/**
 * lib/appMode.js — decides whether to render the real app or the
 * "Under Development" splash for a given deployment.
 *
 * Precedence (highest first):
 *   1. localStorage bypass — the operator can peek at the real app on
 *      a live domain by visiting `<domain>/?bypass=<UNDER_DEV_BYPASS>`
 *      once; the flag persists across reloads for that browser only.
 *   2. Explicit env var  `REACT_APP_APP_MODE`
 *        `"live"`     → force splash
 *        `"preview"`  → force real app
 *   3. Hostname heuristic (default when the env var is unset):
 *        contains `.preview.`, `localhost`, or `127.0.0.1` → real app
 *        anything else                                    → splash
 *
 * This is intentionally client-side only. It's a marketing gate, not a
 * security boundary — the backend routes remain reachable and unchanged.
 */

const BYPASS_TOKEN =
  process.env.REACT_APP_UNDER_DEV_BYPASS || "eengdes-live-preview";
const STORAGE_KEY = "eengdes:under-dev-bypass";

/**
 * Consumes a `?bypass=<token>` query param on load and, if valid,
 * writes a flag to localStorage. Idempotent; safe to call once per
 * page-load at app entry.
 */
export function consumeBypassParamIfPresent() {
  try {
    const q = new URLSearchParams(window.location.search);
    const b = q.get("bypass");
    if (b && b === BYPASS_TOKEN) {
      localStorage.setItem(STORAGE_KEY, "1");
    } else if (b === "off") {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* localStorage / URL failures are non-fatal — just skip. */
  }
}

function hasBypass() {
  try { return localStorage.getItem(STORAGE_KEY) === "1"; }
  catch { return false; }
}

function hostnameSuggestsPreview() {
  const h = (typeof window !== "undefined" && window.location?.hostname) || "";
  return (
    h.includes(".preview.") ||
    h === "localhost" ||
    h === "127.0.0.1" ||
    h.endsWith(".local")
  );
}

export function isUnderDevelopment() {
  // v3.2.38 · App is now fully launched on eengdes.com — no more
  // "under development" splash. Kept the function (and the file's
  // bypass-token machinery) in case we ever want to gate a future
  // pre-release the same way. Flip this to the old logic to bring
  // the splash back.
  return false;
}
